import { pipe } from 'fp-ts/function';
import { array, option, ord, string } from 'fp-ts';
import {
    EmailWhitelistConfiguration,
    GeneralArea,
    GeneralEnterprise,
    useRepositoryMutation,
    useRepositoryQuery,
} from '@mews-commander/data-access';
import { MewsTeam } from '@mews/enums';
import { WithEnterpriseId } from '@mews-commander/types';
import { TableState } from '@optimus-web/core';
import { useLocalizeText } from '@mews/framework';
import { getLocalizedMailTypes } from './helpers';

const meta = {
    logError: {
        team: MewsTeam.GuestExperience,
    },
    disableDefaultErrorHandler: true,
};

interface WithResultCallbacks {
    onError: (error: Error) => void;
    onSuccess?: () => void;
}

export const useDeleteWhitelistedUserEmail = ({ onSuccess, onError }: WithResultCallbacks) =>
    useRepositoryMutation('emailWhitelistConfigurations', 'delete', {
        onSuccess,
        onError,
        invalidateQueries: {
            emailWhitelistConfigurations: ['get'],
        },
        meta,
    });

const containsSearchTerm = (arr: string[], searchTerm: string) =>
    arr.find((item) => item.toLowerCase().includes(searchTerm.toLowerCase())) != null;

interface WhitelistedUserEmailsGetQueryParams extends WithEnterpriseId {
    tableState: TableState<EmailWhitelistConfiguration>;
}

export const useGetWhitelistedUserEmails = ({ enterpriseId, tableState }: WhitelistedUserEmailsGetQueryParams) => {
    const localizeText = useLocalizeText();

    return useRepositoryQuery(
        'emailWhitelistConfigurations',
        'get',
        { enterpriseId },
        {
            meta,
            select: (data) => {
                const filteredConfigurations = data.configurations.filter((configuration) => {
                    const searchTerm = tableState.globalFilters.search;
                    if (searchTerm == null) {
                        return true;
                    }
                    const localizedMailTypes = getLocalizedMailTypes({
                        emailTypes: configuration.emailTypes,
                        localizeText,
                    });

                    return containsSearchTerm(
                        [configuration.email, configuration.name, ...localizedMailTypes],
                        searchTerm
                    );
                });

                return {
                    ...data,
                    configurations: filteredConfigurations,
                };
            },
        }
    );
};

export const useUpdateWhitelistedUserEmails = ({ onSuccess, onError }: WithResultCallbacks) =>
    useRepositoryMutation('emailWhitelistConfigurations', 'update', {
        onSuccess,
        onError,
        invalidateQueries: {
            emailWhitelistConfigurations: ['get'],
        },
        meta,
    });

export const useAddWhitelistedUserEmails = ({ onSuccess, onError }: WithResultCallbacks) =>
    useRepositoryMutation('emailWhitelistConfigurations', 'add', {
        onSuccess,
        onError,
        invalidateQueries: {
            emailWhitelistConfigurations: ['get'],
        },
        meta,
    });

export interface ExtendedGeneralEnterprise extends GeneralEnterprise {
    city: string;
    country: string;
}

interface GetGeneralEnterprisesProps {
    tableState: TableState<GeneralEnterprise>;
}

const sortByName = pipe(
    string.Ord,
    ord.contramap((p: ExtendedGeneralEnterprise) => p.name || '')
);

export const useGetFilteredGeneralEnterprises = ({ tableState }: GetGeneralEnterprisesProps) => {
    const localizeText = useLocalizeText();

    return useRepositoryQuery(
        'generalEnterprises',
        'getAll',
        {
            application: GeneralArea.Commander,
            extent: {
                cities: true,
            },
        },
        {
            meta,
            select: (data) => {
                const searchTerm = tableState.globalFilters.search;
                const cityNames =
                    data?.cities?.reduce((acc: Record<string, string>, city) => {
                        acc[city.id] = city.name;
                        return acc;
                    }, {}) ?? {};
                const filteredExtendedEnterprises = pipe(
                    data.enterprises,
                    array.filterMap((enterprise) => {
                        const extendedEnterprise = {
                            ...enterprise,
                            city: cityNames[enterprise.cityId] || '',
                            country:
                                enterprise.address.countryCode != null
                                    ? localizeText({
                                          textKey: `Country${enterprise.address.countryCode}` as Mews.TextKey,
                                      })
                                    : '',
                        };

                        if (searchTerm == null) {
                            return option.some(extendedEnterprise);
                        }

                        const found = containsSearchTerm(
                            [extendedEnterprise.name, extendedEnterprise.city, extendedEnterprise.country],
                            searchTerm
                        );

                        if (found) {
                            return option.some(extendedEnterprise);
                        }

                        return option.none;
                    })
                );

                return {
                    ...data,
                    enterprises: array.sortBy([sortByName])(filteredExtendedEnterprises),
                };
            },
        }
    );
};
